import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { BasefieldV2Component } from '../basefield-v2/basefield-v2.component';
import { UntypedFormControl } from '@angular/forms';
import { DropdownItem } from '@voyagu/radar-design-system';
import { mapEnumToDropdownItems } from '@app/shared/utils/dropdown-utils';
import { ChargeDirectionType, CostNode, CostPerType } from '@app/generated/graphql';
import IMask from 'imask';
import { combineLatest, delay, map, Observable, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isBoolean } from 'lodash';

@Component({
  selector: 'booking-cost',
  templateUrl: './booking-cost.component.html',
  styleUrls: ['./booking-cost.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingCostComponent extends BasefieldV2Component implements OnInit {
  imaskNumeric: IMask.MaskedNumberOptions;
  imask: IMask.MaskedNumberOptions;

  costCharges$: Observable<DropdownItem[]>;
  costCharges: DropdownItem[] = [];
  selectedCostCharge: DropdownItem;

  perTypes: DropdownItem[] = [];
  selectedPerType: DropdownItem;

  isDirectTypes: DropdownItem[] = [];
  selectedIsDirect: DropdownItem<any> = IsDirectTypes[0];

  costChargeControl = new UntypedFormControl();
  perTypeControl = new UntypedFormControl();
  rateControl = new UntypedFormControl();
  isDirectControl = new UntypedFormControl(IsDirectTypes[0].value);

  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.costCharges$ = this.props.optionsService?.pipe(
      map((costCharges) => {
        this.costCharges = costCharges as DropdownItem[];

        if (this.formControl.value) {
          const cost = this.formControl.value as CostNode;

          this.selectedCostCharge = this.costCharges.find(
            ({ value }) => value == cost.chargeTypeId,
          ) as DropdownItem;
        }

        return this.costCharges;
      }),
    );

    this.perTypes = mapEnumToDropdownItems(CostPerType);

    this.isDirectTypes = IsDirectTypes as DropdownItem<any>[];

    this.imaskNumeric = {
      mask: Number,
      signed: !this.props.positiveOnly,
      scale: this.props.integerOnly ? 0 : 3,
      thousandsSeparator: this.props.delimiter || ' ',
      max: 999999999999,
    };

    this.imask = {
      mask: Number,
      signed: true,
      radix: '.',
      thousandsSeparator: ',',
      scale: this.props.currencyFormat === 'cents' ? 5 : 2,
      padFractionalZeros: true,
      max: 999999999999,
    };

    if (this.formControl.value) {
      const cost = this.formControl.value as CostNode;

      const disabled = this.form.disabled || !!this.props.disabled || !!this.props.readonly;

      if (disabled) {
        this.costChargeControl.disable();
        this.perTypeControl.disable();
        this.rateControl.disable();
        this.isDirectControl.disable();
      }

      this.costChargeControl.setValue(cost.chargeTypeId);
      this.perTypeControl.setValue(cost.perType);
      this.rateControl.setValue(cost.rate);

      this.selectedPerType = this.perTypes.find(
        ({ value }) => value === cost.perType,
      ) as DropdownItem;

      this.selectedIsDirect = isBoolean(cost.isDirect)
        ? (this.isDirectTypes.find(
            ({ value }) => Boolean(value) === Boolean(cost.isDirect),
          ) as DropdownItem)
        : this.props.isDirectPaymentsDefault
          ? IsDirectTypes[0]
          : IsDirectTypes[1];

      this.isDirectControl.setValue(this.selectedIsDirect.value);
    }

    combineLatest([
      this.costChargeControl.valueChanges.pipe(startWith(this.costChargeControl.value)),
      this.perTypeControl.valueChanges.pipe(startWith(this.perTypeControl.value)),
      this.rateControl.valueChanges.pipe(startWith(this.rateControl.value)),
      this.isDirectControl.valueChanges.pipe(startWith(this.isDirectControl.value)),
    ])
      .pipe(
        map(([chargeTypeId, perType, rate, isDirect]) => {
          this.formControl.setValue({
            chargeTypeId,
            perType,
            rate,
            id: this.formControl.value?.id,
            isDirect,
          });
          this.formControl.markAsDirty();
        }),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    combineLatest([
      this.costCharges$,
      this.costChargeControl.valueChanges.pipe(startWith(this.costChargeControl.value)),
    ])
      .pipe(delay(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(([costCharges, chargeTypeId]) => {
        const selectedCostCharge = costCharges.find(
          ({ value }) => value == chargeTypeId,
        ) as DropdownItem;

        const isMoneyIn = selectedCostCharge?.extras?.direction === ChargeDirectionType.MoneyIn;

        if (isMoneyIn) {
          this.selectIsDirect(IsDirectTypes[1]);
          this.isDirectControl.disable();
        } else {
          this.isDirectControl.enable();
        }
      });
  }

  selectCostCharge(costCharge: DropdownItem) {
    this.selectedCostCharge = costCharge;
    this.costChargeControl.setValue(costCharge.value);

    this.selectedIsDirect = this.props.isDirectPaymentsDefault
      ? IsDirectTypes[0]
      : IsDirectTypes[1];
    this.isDirectControl.setValue(this.selectedIsDirect.value);
  }

  selectPerType(perType: DropdownItem) {
    this.selectedPerType = perType;
    this.perTypeControl.setValue(perType.value);
  }

  selectIsDirect(selectedIsDirect: DropdownItem<boolean>) {
    this.selectedIsDirect = selectedIsDirect;
    this.isDirectControl.setValue(selectedIsDirect.value);
  }

  onAccept() {} // without this hack form do not work consistently
}

const IsDirectTypes = [
  { value: true, label: 'True' },
  { value: false, label: 'False' },
];
